import { useCallback, useMemo, useState } from 'react';
import { Box, Button, FormControl, FormLabel, HStack, Heading, Icon, Input, Select, SimpleGrid } from '@chakra-ui/react';
import { GridColDef } from '@mui/x-data-grid';
import { FiFilter, FiX } from 'react-icons/fi';
import {
  DashboardHooks,
  useDeleteSubscriptionMutation,
  useProductsV2,
  useSubscriptions,
} from '@partiu/api/dashboard/hooks.ts';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider.tsx';
import { DataTablePage } from '../components/templates/DataTablePage.tsx';
import { CPFFormRemove } from '../components/forms/CPFFormRemove.tsx';
import { SubscriptionForm } from '../components/forms/SubscriptionForm.tsx';
import {
  DataGridBooleanFormatter,
  DataGridCPFCNPJFormatter,
  DataGridDateFormatter,
} from '../formatters.ts';

type TriState = '' | 'true' | 'false';

interface SubscriptionFiltersState {
  month: string;
  year: string;
  startDate: string;
  validUntil: string;
  createdManually: TriState;
  productId: string;
}

// Antes da API aceitar filtros, a listagem só retornava assinaturas com created_manually=true.
// Mantemos esse valor como padrão para não mudar o comportamento atual da tela.
const DEFAULT_FILTERS: SubscriptionFiltersState = {
  month: '',
  year: '',
  startDate: '',
  validUntil: '',
  createdManually: 'true',
  productId: '',
};

const MONTHS = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

function buildSubscriptionFilters(filters: SubscriptionFiltersState): Record<string, unknown> {
  const params: Record<string, unknown> = {};

  // Data início exata tem prioridade sobre mês/ano, como sugerido na doc de filtros.
  if (filters.startDate) {
    params.start_date = filters.startDate;
  } else {
    if (filters.month) params.month = Number(filters.month);
    if (filters.year) params.year = Number(filters.year);
  }

  if (filters.validUntil) params.valid_until = filters.validUntil;
  if (filters.createdManually !== '') params.created_manually = filters.createdManually === 'true';
  if (filters.productId) params.product_id = Number(filters.productId);

  return params;
}

export function SubscriptionsPage() {
  const { id: agreementId } = useTheme();
  const [filters, setFilters] = useState<SubscriptionFiltersState>(DEFAULT_FILTERS);

  const { data: productsData } = useProductsV2(
    { page_size: 300, include_inactive: true, agreement: agreementId },
    { enabled: !!agreementId, refetchOnMount: false }
  );
  const products = productsData?.results ?? [];

  const handleFilterChange = useCallback(
    <K extends keyof SubscriptionFiltersState>(key: K, value: SubscriptionFiltersState[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleClearFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const extraFilters = useMemo(() => buildSubscriptionFilters(filters), [filters]);

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => currentYear - 4 + i);
  }, []);

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      {
        field: 'externalCode',
        headerName: 'Código Externo',
        width: 160,
        renderCell: ({ value }) =>
          value ? value : <span style={{ opacity: 0.5 }}>Não informado</span>,
      },
      { field: 'cpf', headerName: 'CPF/CNPJ', width: 160, valueFormatter: DataGridCPFCNPJFormatter },
      { field: 'agreement', headerName: 'Convênio', flex: 1, minWidth: 180 },
      {
        field: 'plan',
        headerName: 'Plano / Produto',
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }) => (
          <span>
            {row.plan}
            {row.productName && <span style={{ opacity: 0.6 }}> · {row.productName}</span>}
          </span>
        ),
      },
      { field: 'active', headerName: 'Ativo', width: 110, valueFormatter: DataGridBooleanFormatter },
      { field: 'startDate', headerName: 'Início', width: 140, valueFormatter: DataGridDateFormatter },
      { field: 'validUntil', headerName: 'Expira em', width: 140, valueFormatter: DataGridDateFormatter },
      { field: 'createdAt', headerName: 'Criado em', width: 140, valueFormatter: DataGridDateFormatter },
    ];

    // Combinação da oferta só existe para a Telecall — demais convênios não preenchem esse campo.
    if (agreementId === 2322) {
      cols.push({
        field: 'offerCombination',
        headerName: 'Combinação da Oferta',
        flex: 1,
        minWidth: 200,
        sortable: false,
      });
    }

    return cols;
  }, [agreementId]);

  return (
    <DataTablePage
      modelName={'Subscrições'}
      listQueryKey={DashboardHooks.Subscriptions}
      listHook={useSubscriptions}
      deleteMutationHook={useDeleteSubscriptionMutation}
      removeForm={CPFFormRemove}
      addForm={SubscriptionForm}
      canGenerateReport="subscriptions-dashboard"
      canExport={false}
      selectable={true}
      searchPlaceholder={'CPF / CNPJ / Código'}
      extraFilters={extraFilters}
      filtersSlot={
        <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="lg" p={4} width="100%">
          <HStack mb={3} spacing={2}>
            <Icon as={FiFilter} color="blue.500" />
            <Heading size="sm" color="gray.700">
              Filtros
            </Heading>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="gray"
              leftIcon={<FiX />}
              onClick={handleClearFilters}
              marginLeft="auto"
            >
              Limpar filtros
            </Button>
          </HStack>

          <SimpleGrid columns={{ base: 1, md: 2, xl: 6 }} spacing={4}>
            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Produto
              </FormLabel>
              <Select
                placeholder="Todos"
                value={filters.productId}
                onChange={(e) => handleFilterChange('productId', e.target.value)}
                bg="white"
                variant="filled"
              >
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Mês (início)
              </FormLabel>
              <Select
                placeholder="Todos"
                value={filters.month}
                onChange={(e) => handleFilterChange('month', e.target.value)}
                bg="white"
                variant="filled"
                isDisabled={!!filters.startDate}
              >
                {MONTHS.map((label, index) => (
                  <option key={label} value={index + 1}>
                    {label}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Ano (início)
              </FormLabel>
              <Select
                placeholder="Todos"
                value={filters.year}
                onChange={(e) => handleFilterChange('year', e.target.value)}
                bg="white"
                variant="filled"
                isDisabled={!!filters.startDate}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Data início exata
              </FormLabel>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                bg="white"
                variant="filled"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Validade
              </FormLabel>
              <Input
                type="date"
                value={filters.validUntil}
                onChange={(e) => handleFilterChange('validUntil', e.target.value)}
                bg="white"
                variant="filled"
              />
            </FormControl>

            <FormControl>
              <FormLabel fontSize="xs" color="gray.600" mb={1}>
                Criados manualmente
              </FormLabel>
              <Select
                value={filters.createdManually}
                onChange={(e) => handleFilterChange('createdManually', e.target.value as TriState)}
                bg="white"
                variant="filled"
              >
                <option value="">Todos</option>
                <option value="true">Sim</option>
                <option value="false">Não</option>
              </Select>
            </FormControl>
          </SimpleGrid>
        </Box>
      }
      columns={columns}
    />
  );
}
